// А.4.5: Deck Builder — engine API для сборки колоды Игрока перед матчем.
// Каталог: type ∈ {unit, action, mod}, rarity ∈ {common, rare, epic, legendary, special-у Мода}.
// Special карты Деуса (Влияние, Спящий агент) НЕ в каталоге — они вне колоды.
// Б.1.c-fix1: Моды Синтетика (type='mod', rarity='special') ВКЛЮЧЕНЫ в каталог,
// идут отдельной категорией. Лимит копий = 3, лимит редкостей не занимают,
// общий счёт колоды = 24. Common + Mods = 15 (Mods «забирают» из common-слотов).

import { getFactionCards } from '../data/decks.js';

export const DECK_RECIPE = Object.freeze({ legendary: 1, epic: 2, rare: 6, common: 15 });
export const COPY_LIMIT  = Object.freeze({ legendary: 1, epic: 1, rare: 2, common: 3, special: 3 });
export const DECK_SIZE = 24;

// Каталог карт для Deck Builder. Возвращает массив шаблонов фракции.
export function getCatalog(factionId) {
  const all = getFactionCards(factionId);
  return all
    .filter(c => {
      // Моды Синтетика (type='mod') — пускаем.
      if (c.type === 'mod') return true;
      // Юниты/Действия — да, но не special (Спящий агент Деуса — special юнит, остаётся вне колоды).
      if (c.type === 'unit' || c.type === 'action') return c.rarity !== 'special';
      return false;
    })
    .slice()
    .sort((a, b) => {
      // Сортировка: по редкости (легендарные первыми), Моды в самом низу. Затем по имени.
      const order = { legendary: 0, epic: 1, rare: 2, common: 3, special: 4 };
      const ra = order[a.rarity] ?? 9, rb = order[b.rarity] ?? 9;
      if (ra !== rb) return ra - rb;
      return String(a.name_ru).localeCompare(String(b.name_ru), 'ru');
    });
}

// Подсчёт по редкостям и копиям.
export function deckStats(deck) {
  const byRarity = { legendary: 0, epic: 0, rare: 0, common: 0, special: 0 };
  const copies = new Map();
  for (const c of deck) {
    if (byRarity[c.rarity] === undefined) byRarity[c.rarity] = 0;
    byRarity[c.rarity]++;
    copies.set(c.id, (copies.get(c.id) || 0) + 1);
  }
  return { byRarity, copies };
}

// Можно ли добавить ещё одну копию cardTpl в deck.
// Возвращает {ok, reason}.
// Б.1.c-fix1: Моды (rarity='special') не имеют своей квоты — они «забирают» из common-слотов.
//   Условие: byRarity.common + byRarity.special < DECK_RECIPE.common (15).
export function canAddCard(deck, cardTpl) {
  if (deck.length >= DECK_SIZE) return { ok: false, reason: 'deck_full' };
  const { byRarity, copies } = deckStats(deck);
  const limit = COPY_LIMIT[cardTpl.rarity];
  if (limit == null) return { ok: false, reason: 'unknown_rarity' };
  if (cardTpl.rarity === 'special') {
    // Мод занимает common-слот. Проверяем общий budget.
    const used = (byRarity.common || 0) + (byRarity.special || 0);
    if (used >= DECK_RECIPE.common) return { ok: false, reason: 'rarity_cap' };
  } else if (cardTpl.rarity === 'common') {
    const used = (byRarity.common || 0) + (byRarity.special || 0);
    if (used >= DECK_RECIPE.common) return { ok: false, reason: 'rarity_cap' };
  } else {
    const want = DECK_RECIPE[cardTpl.rarity];
    if (want == null) return { ok: false, reason: 'unknown_rarity' };
    if ((byRarity[cardTpl.rarity] || 0) >= want) return { ok: false, reason: 'rarity_cap' };
  }
  const used = copies.get(cardTpl.id) || 0;
  if (used >= limit) return { ok: false, reason: 'copy_limit' };
  return { ok: true };
}

// Валидация колоды: ровно 24 карт, точное распределение редкостей, лимиты копий.
// Б.1.c-fix1: common + special = 15 (Моды забирают common-слоты).
export function validateDeck(deck) {
  const reasons = [];
  if (deck.length !== DECK_SIZE) {
    reasons.push(`Размер ${deck.length}/${DECK_SIZE}`);
  }
  const { byRarity, copies } = deckStats(deck);
  for (const r of ['legendary', 'epic', 'rare']) {
    const want = DECK_RECIPE[r];
    const have = byRarity[r] || 0;
    if (have !== want) reasons.push(`${rarityRu(r)}: ${have}/${want}`);
  }
  const commonPlusSpecial = (byRarity.common || 0) + (byRarity.special || 0);
  if (commonPlusSpecial !== DECK_RECIPE.common) {
    reasons.push(`Обычные + Моды: ${commonPlusSpecial}/${DECK_RECIPE.common}`);
  }
  for (const [id, n] of copies) {
    const card = deck.find(c => c.id === id);
    if (!card) continue;
    const lim = COPY_LIMIT[card.rarity];
    if (lim != null && n > lim) reasons.push(`${id}: ${n} копий (лимит ${lim})`);
  }
  return { ok: reasons.length === 0, reasons };
}

// Случайная валидная колода фракции — 1+2+6+15 + лимиты копий.
// Возвращает массив шаблонов (не Card-инстансов).
export function buildRandomDeck(factionId, rng) {
  const catalog = getCatalog(factionId);
  return fillRemaining([], factionId, rng, catalog);
}

// Дополнить colode currentDeck до полной (24) с учётом уже выбранных.
// Соблюдает лимиты редкости и копий.
// Б.1.c-fix1: после legendary/epic/rare идёт совместный common+special-блок (15 слотов),
// у Синтетика это обычные + Моды; у Деуса — только обычные.
// catalog — опционально предвычисленный каталог (для оптимизации).
export function fillRemaining(currentDeck, factionId, rng, catalog = null) {
  const cat = catalog || getCatalog(factionId);
  const deck = [...currentDeck];
  // Сначала legendary/epic/rare — точные квоты.
  for (const rarity of ['legendary', 'epic', 'rare']) {
    const want = DECK_RECIPE[rarity];
    const have = deckStats(deck).byRarity[rarity] || 0;
    let need = want - have;
    if (need <= 0) continue;
    const pool = cat.filter(c => c.rarity === rarity);
    if (pool.length === 0) continue;
    let safety = 200;
    while (need > 0 && safety-- > 0) {
      const tpl = pool[rng.int(pool.length)];
      if (canAddCard(deck, tpl).ok) { deck.push(tpl); need--; }
    }
  }
  // Затем common + special (Моды Синтетика). Тянем случайно из объединённого пула.
  const lowPool = cat.filter(c => c.rarity === 'common' || c.rarity === 'special');
  if (lowPool.length > 0) {
    let safety = 400;
    while (deck.length < DECK_SIZE && safety-- > 0) {
      const stats = deckStats(deck);
      if ((stats.byRarity.common || 0) + (stats.byRarity.special || 0) >= DECK_RECIPE.common) break;
      const tpl = lowPool[rng.int(lowPool.length)];
      if (canAddCard(deck, tpl).ok) deck.push(tpl);
    }
  }
  return deck;
}

// Случайный Герой фракции.
export function pickRandomHero(factionId, rng) {
  // Импорт через лениво обёрнутый require, чтобы не плодить циклы.
  // Здесь — просто сторонний import.
  // (Для упрощения: пусть caller импортирует из data/heroes.js — этот хелпер тут не нужен.)
  return null;
}

function rarityRu(r) {
  return ({ common: 'Обычные', rare: 'Редкие', epic: 'Эпические', legendary: 'Легендарные' })[r] || r;
}
